/**
 * ForgotPasswordPage - Password reset flow using Firebase
 */

import { useState, FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

export const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const { resetPassword } = useAuth();

  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleResetPassword = async (e: FormEvent) => {
    e.preventDefault();
    try {
      setError('');
      setSuccess(false);
      setLoading(true);
      await resetPassword(email);
      setSuccess(true);
      setEmail('');
    } catch (err: any) {
      setError(err.message || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white">
      <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_20%_20%,rgba(34,211,238,0.25),transparent_35%),radial-gradient(circle_at_80%_0%,rgba(59,130,246,0.25),transparent_30%),radial-gradient(circle_at_50%_80%,rgba(14,165,233,0.2),transparent_30%)]" />

      <div className="relative max-w-6xl mx-auto px-6 py-16">
        <div className="flex items-center justify-between mb-10">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center shadow-lg shadow-cyan-500/20">
              <span className="text-lg font-bold">SP</span>
            </div>
            <div>
              <p className="text-sm text-slate-300">Welcome back</p>
              <p className="text-2xl font-semibold">Smart Prompt</p>
            </div>
          </Link>
          <div className="hidden md:flex items-center gap-2 text-sm text-slate-400 bg-white/5 border border-slate-800 px-4 py-2 rounded-full">
            <span className="inline-flex h-2 w-2 rounded-full bg-emerald-400 animate-pulse" aria-hidden="true" />
            Secure workspace — encrypted auth
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 items-stretch">
          <div className="bg-white/5 border border-slate-800 rounded-2xl p-8 shadow-2xl backdrop-blur-xl flex flex-col justify-between">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-400/10 text-blue-200 text-xs font-semibold border border-blue-400/30">
                <span className="inline-flex h-2 w-2 rounded-full bg-blue-400" aria-hidden="true" />
                Reset password
              </div>
              <h1 className="text-4xl font-bold leading-tight">Reset your password</h1>
              <p className="text-lg text-slate-300">
                Enter your email address and we'll send you a link to reset your password. Check your email for instructions.
              </p>
              <div className="grid sm:grid-cols-2 gap-3 pt-2">
                {["Instant reset link", "Secure verification", "Valid for 24 hours", "Easy recovery process"].map((item) => (
                  <div key={item} className="flex items-center gap-2 text-sm text-slate-200 bg-slate-900/40 border border-slate-800 rounded-lg px-3 py-2">
                    <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-8 text-sm text-slate-400 bg-slate-900/40 border border-slate-800 rounded-xl p-4">
              <p>
                Remember your password?{' '}
                <Link to="/login" className="font-semibold text-cyan-300 hover:text-cyan-200">
                  Back to sign in
                </Link>
              </p>
            </div>
          </div>

          <div className="bg-slate-950/60 border border-slate-800 rounded-2xl p-8 shadow-2xl backdrop-blur-2xl">
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="text-sm text-slate-400">Password recovery</p>
                <h2 className="text-2xl font-semibold">Reset password</h2>
              </div>
              <Link to="/login" className="text-sm text-cyan-300 hover:text-cyan-200 font-semibold">
                Sign in
              </Link>
            </div>

            {error && (
              <div className="rounded-xl border border-red-500/40 bg-red-500/10 text-red-100 px-4 py-3 mb-4">
                <div className="text-sm font-medium">{error}</div>
              </div>
            )}

            {success && (
              <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 text-emerald-100 px-4 py-3 mb-4">
                <div className="text-sm font-medium">
                  Check your email for a password reset link. The link is valid for 24 hours.
                </div>
              </div>
            )}

            <form onSubmit={handleResetPassword} className="space-y-5">
              <div className="space-y-2">
                <label htmlFor="email" className="block text-sm font-medium text-slate-200">
                  Email address
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={success}
                  className="mt-1 block w-full px-4 py-3 rounded-xl bg-slate-900/60 border border-slate-800 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400 disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="you@example.com"
                />
              </div>

              <button
                type="submit"
                disabled={loading || success}
                className="w-full flex justify-center items-center gap-2 py-3 px-4 rounded-xl font-semibold text-white bg-gradient-to-r from-cyan-500 to-blue-500 shadow-lg shadow-cyan-500/20 hover:from-cyan-400 hover:to-blue-400 focus:outline-none focus:ring-2 focus:ring-cyan-300 focus:ring-offset-2 focus:ring-offset-slate-950 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Sending reset link…' : success ? 'Email sent!' : 'Send reset link'}
              </button>
            </form>

            <p className="text-center text-sm text-slate-400 mt-6">
              Don't have an account?{' '}
              <Link to="/signup" className="font-semibold text-cyan-300 hover:text-cyan-200">
                Create one here
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
